import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ClerkProvider, SignedIn, SignedOut } from '@clerk/clerk-react';
import { ConvexProvider, ConvexReactClient } from 'convex/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { ThemeProvider } from './contexts/ThemeContext';
import { AdminProvider } from './contexts/AdminContext';

// Pages
import LoginPage from './pages/auth/LoginPage';
import UnauthorizedPage from './pages/auth/UnauthorizedPage';
import Dashboard from './pages/Dashboard';
import UserList from './pages/users/UserList';
import UserDetail from './pages/users/UserDetail';
import VerificationList from './pages/verifications/VerificationList';
import VerificationDetail from './pages/verifications/VerificationDetail';
import CommunityList from './pages/communities/CommunityList';
import CommunityDetail from './pages/communities/CommunityDetail';
import ReportsList from './pages/moderation/ReportsList';
import ReportDetail from './pages/moderation/ReportDetail';
import DeletedContent from './pages/moderation/DeletedContent';
import ReservedUsernames from './pages/ReservedUsernames';
import Analytics from './pages/Analytics';
import AdminList from './pages/admins/AdminList';
import AdminDetail from './pages/admins/AdminDetail';
import AddAdmin from './pages/admins/AddAdmin';
import SystemConfig from './pages/SystemConfig';
import SecurityConfig from './pages/SecurityConfig';
import PopupManager from './pages/PopupManager';
import ActivityLogs from './pages/ActivityLogs';
import TicketList from './pages/support/TicketList';
import TicketDetail from './pages/support/TicketDetail';
import CreateTicket from './pages/support/CreateTicket';
import TagManager from './pages/support/TagManager';
import LBASettings from './pages/LBASettings';
import Maintenance from './pages/Maintenance';
import NotificationsManager from './pages/NotificationsManager';

// Basketball
import GamesManagement from './pages/basketball/GamesManagement';
import TeamsManagement from './pages/basketball/TeamsManagement';
import TeamRosterManagement from './pages/basketball/TeamRosterManagement';
import CustomPeopleManagement from './pages/basketball/CustomPeopleManagement';
import StandingsManagement from './pages/basketball/StandingsManagement';
import BasketballSettings from './pages/basketball/BasketballSettings';

// Layout
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import SmartRedirect from './components/SmartRedirect';

const clerkPubKey = import.meta.env.VITE_ADMIN_CLERK_PUBLISHABLE_KEY;
const adminConvexUrl = import.meta.env.VITE_ADMIN_CONVEX_URL;

if (!clerkPubKey) {
    throw new Error('Missing VITE_ADMIN_CLERK_PUBLISHABLE_KEY');
}

if (!adminConvexUrl) {
    throw new Error('Missing VITE_ADMIN_CONVEX_URL');
}

const convexClient = new ConvexReactClient(adminConvexUrl);
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 60 * 1000, // 1 minute
            refetchOnWindowFocus: false,
        },
    },
});

function App() {
    return (
        <ClerkProvider publishableKey={clerkPubKey}>
            <ConvexProvider client={convexClient}>
                <QueryClientProvider client={queryClient}>
                    <ThemeProvider>
                        <BrowserRouter>
                            <SignedOut>
                                <Routes>
                                    <Route path="/login" element={<LoginPage />} />
                                    <Route path="/unauthorized" element={<UnauthorizedPage />} />
                                    <Route path="*" element={<Navigate to="/login" replace />} />
                                </Routes>
                            </SignedOut>

                            <SignedIn>
                                <AdminProvider>
                                    <Routes>
                                        <Route path="/unauthorized" element={<UnauthorizedPage />} />
                                        <Route path="/" element={<Layout />}>
                                            {/* Smart redirect for index - sends users to their appropriate section */}
                                            <Route index element={
                                                <SmartRedirect>
                                                    <Dashboard />
                                                </SmartRedirect>
                                            } />

                                            {/* User Management */}
                                            <Route path="users" element={<ProtectedRoute privilege="user_management"><UserList /></ProtectedRoute>} />
                                            <Route path="users/:userId" element={<ProtectedRoute privilege="user_management"><UserDetail /></ProtectedRoute>} />

                                            {/* Verification Requests */}
                                            <Route path="verifications" element={<ProtectedRoute privilege="verification_approval"><VerificationList /></ProtectedRoute>} />
                                            <Route path="verifications/:requestId" element={<ProtectedRoute privilege="verification_approval"><VerificationDetail /></ProtectedRoute>} />

                                            {/* Community Requests */}
                                            <Route path="communities" element={<ProtectedRoute privilege="verification_approval"><CommunityList /></ProtectedRoute>} />
                                            <Route path="communities/:requestId" element={<ProtectedRoute privilege="verification_approval"><CommunityDetail /></ProtectedRoute>} />

                                            {/* Content Moderation */}
                                            <Route path="reports" element={<ProtectedRoute privilege="content_moderation"><ReportsList /></ProtectedRoute>} />
                                            <Route path="reports/:reportId" element={<ProtectedRoute privilege="content_moderation"><ReportDetail /></ProtectedRoute>} />
                                            <Route path="deleted-content" element={<ProtectedRoute privilege="content_moderation"><DeletedContent /></ProtectedRoute>} />

                                            {/* Reserved Usernames */}
                                            <Route path="reserved-usernames" element={<ProtectedRoute privilege="user_management"><ReservedUsernames /></ProtectedRoute>} />

                                            {/* Analytics */}
                                            <Route path="analytics" element={<ProtectedRoute privilege="analytics_view"><Analytics /></ProtectedRoute>} />

                                            {/* Admin Management */}
                                            <Route path="admins" element={<ProtectedRoute privilege="super_admin"><AdminList /></ProtectedRoute>} />
                                            <Route path="admins/add" element={<ProtectedRoute privilege="super_admin"><AddAdmin /></ProtectedRoute>} />
                                            <Route path="admins/:adminId" element={<ProtectedRoute privilege="super_admin"><AdminDetail /></ProtectedRoute>} />

                                            {/* System */}
                                            <Route path="system" element={<ProtectedRoute privilege="system_config"><SystemConfig /></ProtectedRoute>} />
                                            <Route path="security" element={<ProtectedRoute privilege="system_config"><SecurityConfig /></ProtectedRoute>} />
                                            <Route path="popups" element={<ProtectedRoute privilege="system_config"><PopupManager /></ProtectedRoute>} />

                                            {/* Activity Logs - super admin only */}
                                            <Route path="activity-logs" element={<ProtectedRoute privilege="super_admin"><ActivityLogs /></ProtectedRoute>} />

                                            {/* Support Tickets - requires user_management */}
                                            <Route path="support" element={<ProtectedRoute privilege="user_management"><TicketList /></ProtectedRoute>} />
                                            <Route path="support/create" element={<ProtectedRoute privilege="user_management"><CreateTicket /></ProtectedRoute>} />
                                            <Route path="support/tags" element={<ProtectedRoute privilege="user_management"><TagManager /></ProtectedRoute>} />
                                            <Route path="support/:ticketId" element={<ProtectedRoute privilege="user_management"><TicketDetail /></ProtectedRoute>} />

                                            {/* LBA Account Settings */}
                                            <Route path="lba-settings" element={<ProtectedRoute privilege="super_admin"><LBASettings /></ProtectedRoute>} />

                                            {/* Maintenance */}
                                            <Route path="maintenance" element={<ProtectedRoute privilege="super_admin"><Maintenance /></ProtectedRoute>} />

                                            {/* Notifications */}
                                            <Route path="notifications" element={<ProtectedRoute anyOf={['notification_management', 'super_admin']}><NotificationsManager /></ProtectedRoute>} />

                                            {/* Basketball Management */}
                                            <Route path="basketball/games" element={<ProtectedRoute privilege="basketball_management"><GamesManagement /></ProtectedRoute>} />
                                            <Route path="basketball/teams" element={<ProtectedRoute privilege="basketball_management"><TeamsManagement /></ProtectedRoute>} />
                                            <Route path="basketball/teams/:teamId/roster" element={<ProtectedRoute privilege="basketball_management"><TeamRosterManagement /></ProtectedRoute>} />
                                            <Route path="basketball/people" element={<ProtectedRoute privilege="basketball_management"><CustomPeopleManagement /></ProtectedRoute>} />
                                            <Route path="basketball/standings" element={<ProtectedRoute privilege="basketball_management"><StandingsManagement /></ProtectedRoute>} />
                                            <Route path="basketball/settings" element={<ProtectedRoute privilege="basketball_management"><BasketballSettings /></ProtectedRoute>} />
                                        </Route>

                                        <Route path="/login" element={<Navigate to="/" replace />} />
                                        <Route path="*" element={<Navigate to="/" replace />} />
                                    </Routes>
                                </AdminProvider>
                            </SignedIn>
                        </BrowserRouter>

                        <Toaster
                            position="top-right"
                            toastOptions={{
                                duration: 4000,
                                style: {
                                    background: 'var(--toast-bg)',
                                    color: 'var(--toast-color)',
                                },
                            }}
                        />
                    </ThemeProvider>
                </QueryClientProvider>
            </ConvexProvider>
        </ClerkProvider>
    );
}

export default App;

